/**
 * Explore drawer loading cards as three-reel slot machines (display-only).
 *
 * While the casino insight data loads, each placeholder card in the drawer is a small
 * three-reel slot machine. The reels spin and every so often land on a classic symbol
 * (7, diamond, cherry, bell, BAR, star, lemon, JACKPOT, the SlotCheck check and the
 * current casino's logo), then spin again. When the data is ready the TOP card lands
 * check-check-check, a gold halo sweeps down the drawer and the real EGM cards come in.
 *
 * This file is the pure part: symbols, the random picks, the casino logo choice, the
 * finale timing and which finale plays. No React, no DOM, no data path. The DOM engine
 * is HotListReport/components/slotReelEngine.ts, the lifecycle hook is in
 * HotListReport/components/ExploreLoadingSkeleton.tsx and the CSS is
 * src/styles/_modules/_m-explore-skeleton.scss.
 */
import { SKELETON_CARD_HEIGHT_PX, SKELETON_CARD_MARGIN_PX } from './exploreSkeleton';

// ---------------------------------------------------------------------------
// Symbols
// ---------------------------------------------------------------------------

export type ReelSymbol =
  | 'seven'
  | 'diamond'
  | 'cherry'
  | 'bell'
  | 'bar'
  | 'star'
  | 'lemon'
  | 'jackpot'
  | 'check'
  | 'casino';

export type ReelLine = [ReelSymbol, ReelSymbol, ReelSymbol];

/** Always on the reels. `casino` (the current casino's logo) is added only when it has one. */
export const BASE_REEL_SYMBOLS: ReelSymbol[] = [
  'seven',
  'diamond',
  'cherry',
  'bell',
  'bar',
  'star',
  'lemon',
  'jackpot',
  'check',
];

/** Relative chance of each symbol on a landing. */
export const REEL_SYMBOL_WEIGHTS: Record<ReelSymbol, number> = {
  seven: 1,
  diamond: 1.2,
  cherry: 1.4,
  bell: 1.2,
  bar: 1.2,
  star: 1,
  lemon: 1.2,
  jackpot: 0.8,
  check: 1.1,
  casino: 1.1,
};

/** The finale line. Never lands three-across while loading. */
export const WIN_SYMBOL: ReelSymbol = 'check';

/** Share of loading landings that are three of a kind (never three checks). */
export const LOADING_TRIPLE_CHANCE = 0.12;

export type CasinoLogoSource =
  | {
      logos?: {
        logoAlt?: string | null;
        navBar?: string | null;
      } | null;
    }
  | null
  | undefined;

const cleanSrc = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : null;

/**
 * The current casino's logo for the reels: `logos.logoAlt` (the light logo the header
 * uses on dark backgrounds), else `logos.navBar`, else none (the symbol is left out).
 * Read from the casino the user is on; no casino list.
 */
export function pickCasinoLogoSrc(casino: CasinoLogoSource): string | null {
  const logos = casino?.logos;
  if (!logos) return null;
  return cleanSrc(logos.logoAlt) ?? cleanSrc(logos.navBar);
}

/** Symbols on the reels for this casino. */
export function reelSymbolsFor(logoSrc: string | null | undefined): ReelSymbol[] {
  return cleanSrc(logoSrc) ? [...BASE_REEL_SYMBOLS, 'casino'] : BASE_REEL_SYMBOLS.slice();
}

/** Random source in [0, 1). Injected so tests are deterministic. */
export type Rand = () => number;

/** Weighted pick from `symbols`, optionally never `exclude`. */
export function pickReelSymbol(
  symbols: ReelSymbol[],
  rand: Rand,
  exclude?: ReelSymbol
): ReelSymbol {
  const pool = symbols.filter((s) => s !== exclude);
  const list = pool.length > 0 ? pool : symbols;
  const total = list.reduce((sum, s) => sum + REEL_SYMBOL_WEIGHTS[s], 0);
  let r = rand() * total;
  for (let i = 0; i < list.length; i += 1) {
    r -= REEL_SYMBOL_WEIGHTS[list[i]];
    if (r < 0) return list[i];
  }
  return list[list.length - 1];
}

export function isWinningLine(line: ReelSymbol[]): boolean {
  return line.length === 3 && line.every((s) => s === WIN_SYMBOL);
}

/**
 * One random landing while loading: about 1 in 8 is three of a kind (any symbol except
 * the check); otherwise three independent picks, never check-check-check.
 */
export function pickLoadingLanding(symbols: ReelSymbol[], rand: Rand): ReelLine {
  if (rand() < LOADING_TRIPLE_CHANCE) {
    const s = pickReelSymbol(symbols, rand, WIN_SYMBOL);
    return [s, s, s];
  }
  const a = pickReelSymbol(symbols, rand);
  const b = pickReelSymbol(symbols, rand);
  const c =
    a === WIN_SYMBOL && b === WIN_SYMBOL
      ? pickReelSymbol(symbols, rand, WIN_SYMBOL)
      : pickReelSymbol(symbols, rand);
  return [a, b, c];
}

/** A reel strip: every symbol once plus a few weighted extras, shuffled. */
export function makeReelStrip(symbols: ReelSymbol[], rand: Rand, extras = 3): ReelSymbol[] {
  const strip = symbols.slice();
  for (let i = 0; i < extras; i += 1) strip.push(pickReelSymbol(symbols, rand));
  for (let i = strip.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const t = strip[i];
    strip[i] = strip[j];
    strip[j] = t;
  }
  return strip;
}

// ---------------------------------------------------------------------------
// Reel motion (shared by the engine and the timing below)
// ---------------------------------------------------------------------------

/** Spin speed. */
export const REEL_SYMBOLS_PER_SEC = 15;
/** Start: a small kick back, then up to speed. */
export const REEL_KICK_BACK = 0.22;
export const REEL_KICK_MS = 120;
export const REEL_ACCEL_MS = 250;
/** Stop: ease-out with a slight overshoot (easeOutBack, this overshoot constant). */
export const REEL_STOP_OVERSHOOT = 0.8;
/** easeOutBack'(0): ties the stop's starting speed to the spin speed. */
export const REEL_STOP_SPEED_FACTOR = 3 * (REEL_STOP_OVERSHOOT + 1) - 2 * REEL_STOP_OVERSHOOT;
/**
 * A stop lands on the first whole symbol at least this far ahead (in symbols), so the
 * symbol it lands on is always out of view when it is chosen.
 */
export const REEL_STOP_MIN_SYMBOLS = 1.6;
/** Loading landings: one every 1.1-2.4s per card, reels 0.2s apart, shown 0.6-1.1s. */
export const LOADING_SPIN_MS: [number, number] = [1100, 2400];
export const LOADING_STOP_STAGGER_MS = 200;
export const LOADING_STOP_MS = 550;
export const LOADING_HOLD_MS: [number, number] = [600, 1100];
/** Reduced motion: one reel cross-fades to a new symbol every 1.1-2s per card. */
export const REDUCED_SWAP_MS: [number, number] = [1100, 2000];
export const REDUCED_FADE_MS = 250;

/** Symbols a stop travels, and how long it takes, for a requested stop time. */
export function reelStopDistance(stopMs: number): number {
  return Math.max(
    REEL_STOP_MIN_SYMBOLS,
    (REEL_SYMBOLS_PER_SEC * (stopMs / 1000)) / REEL_STOP_SPEED_FACTOR
  );
}
export function reelStopDurationMs(distance: number): number {
  return (REEL_STOP_SPEED_FACTOR * distance * 1000) / REEL_SYMBOLS_PER_SEC;
}
/**
 * Longest a stop can take: it lands on the next WHOLE symbol past the stop distance,
 * so up to one more symbol than the minimum.
 */
export function reelStopMaxMs(stopMs: number): number {
  return reelStopDurationMs(reelStopDistance(stopMs) + 1);
}

/** easeOutBack with REEL_STOP_OVERSHOOT. */
export function reelStopEase(u: number): number {
  const v = Math.min(1, Math.max(0, u)) - 1;
  return 1 + (REEL_STOP_OVERSHOOT + 1) * v * v * v + REEL_STOP_OVERSHOOT * v * v;
}

// ---------------------------------------------------------------------------
// Finale
// ---------------------------------------------------------------------------

/**
 * - `full`: first finale of the session (about 2.8s): top card lands ✓ ✓ ✓, glows,
 *   gold halo sweeps the drawer, cards come in.
 * - `short`: repeat loads in the same session (timeframe / insight change) and loads
 *   that only showed the reels briefly (about 1.3s).
 * - `reduced`: prefers-reduced-motion: cross-fade to ✓ ✓ ✓, a gold tint, cards fade in.
 * - `none`: no finale (today's cross-fade). Used while the post-login Random Outcomes
 *   drawer holds or reveals the cards, so the !2158 Accept hand-off is unchanged.
 */
export type ReelFinaleKind = 'full' | 'short' | 'reduced' | 'none';
export type PlayingReelFinale = Exclude<ReelFinaleKind, 'none'>;

/** Pitch of a card in the list (80px card + 8px collapsed margin). */
export const REEL_ROW_PITCH_PX = SKELETON_CARD_HEIGHT_PX + SKELETON_CARD_MARGIN_PX;
/** Rows the halo covers (the overlay renders this many cards). */
export const REEL_FINALE_ROWS = 8;
/** The overlay's own fade-out (SKELETON_LEAVE_MS in the CSS). */
export const REEL_OVERLAY_LEAVE_MS = 240;
/** Loads whose reels were visible for less than this get the short finale. */
export const SHORT_FINALE_IF_SHOWN_UNDER_MS = 700;

export type ReelFinaleTiming = {
  /** When each top-card reel is told to stop on the check (left to right). */
  topStopAtMs: [number, number, number];
  /** Requested stop time of those reels. */
  stopMs: number;
  /** Halo starts at the top card; real cards start revealing behind it. */
  haloStartMs: number;
  /** Halo travel time per card row (also the real-card stagger, see haloReachesRowMs). */
  haloRowStepMs: number;
  /** A loading card's gold flash + fade once the halo reaches it. */
  cardOutMs: number;
  /** A real card's fade-in once the halo reaches it. */
  rowRevealMs: number;
  /** Insight + Timeframe sections slide back in (skeleton `visible` goes false). */
  sectionsInMs: number;
  /** Overlay removed. */
  unmountMs: number;
  /** Reveal classes removed from the list. */
  revealEndMs: number;
};

type FinaleInput = {
  topStopAtMs: [number, number, number];
  stopMs: number;
  haloStartMs: number;
  haloRowStepMs: number;
  cardOutMs: number;
  rowRevealMs: number;
  /** Sections slide in this far through the halo sweep (0-1). */
  sectionsAt: number;
};

function buildFinaleTiming(i: FinaleInput): ReelFinaleTiming {
  // Row k is reached at haloStart + (k + 1) * step, so the last of the 8 rows at + 8 steps.
  const sweepMs = REEL_FINALE_ROWS * i.haloRowStepMs;
  const sectionsInMs = Math.round(i.haloStartMs + sweepMs * i.sectionsAt);
  const unmountMs =
    Math.max(i.haloStartMs + sweepMs + i.cardOutMs, sectionsInMs + REEL_OVERLAY_LEAVE_MS) + 60;
  const revealEndMs = Math.max(unmountMs, i.haloStartMs + sweepMs + i.rowRevealMs + 100);
  return {
    topStopAtMs: i.topStopAtMs,
    stopMs: i.stopMs,
    haloStartMs: i.haloStartMs,
    haloRowStepMs: i.haloRowStepMs,
    cardOutMs: i.cardOutMs,
    rowRevealMs: i.rowRevealMs,
    sectionsInMs,
    unmountMs,
    revealEndMs,
  };
}

export const REEL_FINALE_TIMING: Record<PlayingReelFinale, ReelFinaleTiming> = {
  full: buildFinaleTiming({
    topStopAtMs: [250, 480, 710],
    stopMs: 450,
    haloStartMs: 1500,
    haloRowStepMs: 110,
    cardOutMs: 320,
    rowRevealMs: 320,
    sectionsAt: 0.6,
  }),
  short: buildFinaleTiming({
    topStopAtMs: [0, 60, 120],
    stopMs: 300,
    haloStartMs: 780,
    haloRowStepMs: 30,
    cardOutMs: 200,
    rowRevealMs: 220,
    sectionsAt: 0.6,
  }),
  reduced: buildFinaleTiming({
    topStopAtMs: [0, 120, 240],
    stopMs: REDUCED_FADE_MS,
    haloStartMs: 500,
    haloRowStepMs: 0,
    cardOutMs: REDUCED_FADE_MS,
    rowRevealMs: 320,
    sectionsAt: 0,
  }),
};

export function getReelFinaleTiming(kind: PlayingReelFinale): ReelFinaleTiming {
  return REEL_FINALE_TIMING[kind];
}

/** Latest moment the top card's third reel can be resting on the check. */
export function latestTopLandingMs(kind: PlayingReelFinale): number {
  const t = REEL_FINALE_TIMING[kind];
  if (kind === 'reduced') return t.topStopAtMs[2] + REDUCED_FADE_MS;
  return t.topStopAtMs[2] + reelStopMaxMs(t.stopMs);
}

/**
 * When the halo reaches card `index` (0 = top): that loading card flashes and leaves and
 * its real card fades in. The halo starts one row above the top card so the winning
 * card keeps its glow for a beat.
 */
export function haloReachesRowMs(kind: PlayingReelFinale, index: number): number {
  const t = REEL_FINALE_TIMING[kind];
  return t.haloStartMs + (Math.max(0, index) + 1) * t.haloRowStepMs;
}

/**
 * Which finale plays when the data lands after the reels were on screen.
 * Never adds a finale while the post-login drawer holds / reveals the cards.
 */
export function selectReelFinale(input: {
  reducedMotion: boolean;
  loginCardsHeld: boolean;
  fullPlayedThisSession: boolean;
  shownForMs: number;
}): ReelFinaleKind {
  if (input.loginCardsHeld) return 'none';
  if (input.reducedMotion) return 'reduced';
  if (input.fullPlayedThisSession) return 'short';
  if (!(input.shownForMs >= SHORT_FINALE_IF_SHOWN_UNDER_MS)) return 'short';
  return 'full';
}

// ---------------------------------------------------------------------------
// "Full finale already played" for this session (sessionStorage, like the login reveal).
// ---------------------------------------------------------------------------

export const REEL_FINALE_SESSION_KEY = 'slotcheck.reelFinalePlayed';

function defaultSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

export function hasPlayedFullReelFinale(
  storage: Storage | null = defaultSessionStorage()
): boolean {
  try {
    return storage?.getItem(REEL_FINALE_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function markFullReelFinalePlayed(
  storage: Storage | null = defaultSessionStorage()
): void {
  try {
    storage?.setItem(REEL_FINALE_SESSION_KEY, '1');
  } catch {
    // blocked storage: every finale is a full one, which is harmless
  }
}
